/**
 * Port of the legacy `parseCurl` helper. Accepts a raw cURL string and
 * extracts the URL, method, headers map, and body string. Handles:
 *   - `-X` method, `-G` GET flag
 *   - `-H` / `--header` repeating
 *   - `-d` / `--data` / `--data-raw` / `--data-binary` / `--form` / `-F`
 *   - `--data-urlencode` (appended to URL for GET requests)
 *   - multi-line cURL with `\` line continuations
 *   - invisible chars (NBSP, zero-width)
 *   - JSON body normalization (re-stringified to drop excess whitespace)
 *
 * Returns sensible defaults on any error; never throws.
 */
export interface ParsedCurl {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
}

export function parseCurl(curl: string | null | undefined): ParsedCurl {
  try {
    if (!curl || typeof curl !== 'string') {
      return { url: '', method: 'GET', headers: {}, body: '' };
    }

    const s = curl
      .replace(/\u00A0/g, ' ') // non-breaking space → space
      .replace(/\u200B/g, '') // zero-width space → drop
      .replace(/\s{2,}/g, ' ')
      .replace(/\\\r?\n/g, ' ')
      .trim();

    let url = '';
    const urlMatch = s.match(/(https?:\/\/[^\s'"]+)/i);
    if (urlMatch?.[1]) {
      url = urlMatch[1].trim().replace(/'+$/, '');
      try {
        url = decodeURIComponent(url);
      } catch {
        // leave as-is
      }
    }

    let method = 'GET';
    const methodMatch = s.match(/-X\s+(\w+)/i);
    if (methodMatch?.[1]) method = methodMatch[1].toUpperCase();
    if (/\s-G\s/.test(s)) method = 'GET';

    const hasDataFlag =
      /\s(--data|-d|--data-raw|--data-binary|--form|-F|--data-urlencode)\s+/.test(s);
    if (!methodMatch && !/\s-G\s/.test(s) && hasDataFlag) method = 'POST';

    const headers: Record<string, string> = {};
    const headerMatches = [
      ...s.matchAll(/(?:-H|--header)\s+['"]?([^'"\n]+)['"]?/gi),
    ];
    for (const m of headerMatches) {
      const captured = m[1];
      if (!captured) continue;
      const line = captured.trim();
      const [key, ...rest] = line.split(':');
      if (key && rest.length) headers[key.trim()] = rest.join(':').trim();
    }

    const dataFlags = ['-d', '--data', '--data-raw', '--data-binary', '--form', '-F'];
    let body = '';
    for (const flag of dataFlags) {
      const matchQuoted = s.match(
        new RegExp(`${flag}\\s+(['"])([\\s\\S]*?)\\1`),
      );
      if (matchQuoted?.[2]) {
        body = matchQuoted[2].trim();
        break;
      }
      const matchUnquoted = s.match(new RegExp(`${flag}\\s+([^\\s]+)`));
      if (matchUnquoted?.[1]) {
        body = matchUnquoted[1].trim();
        break;
      }
    }

    const params: string[] = [];
    const urlEncodeRegex =
      /--data-urlencode\s+(?:'([^']+)'|"([^"]+)"|([^\s]+))/gi;
    let encoded: RegExpExecArray | null;
    while ((encoded = urlEncodeRegex.exec(s)) !== null) {
      const param = encoded[1] || encoded[2] || encoded[3];
      if (param) params.push(param.trim());
    }
    if (method === 'GET' && params.length) {
      const query = params.join('&');
      url += (url.includes('?') ? '&' : '?') + query;
    }

    if (body && method !== 'GET') {
      try {
        const parsed = JSON.parse(body);
        body = JSON.stringify(parsed);
      } catch {
        // not JSON; leave verbatim
      }
    }

    return { url, method, headers, body };
  } catch {
    return { url: '', method: 'GET', headers: {}, body: '' };
  }
}

/**
 * Recursively try to JSON.parse any string that *looks* like JSON, so a
 * nested-JSON-as-string field becomes a real object before it's stringified
 * back for the wire. Matches legacy `parseNestedStrings` behavior.
 */
export function parseNestedStrings(input: unknown): unknown {
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return input;
    try {
      return parseNestedStrings(JSON.parse(trimmed));
    } catch {
      return input;
    }
  }
  if (Array.isArray(input)) {
    return input.map(parseNestedStrings);
  }
  if (input && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>).map(([k, v]) => [
        k,
        parseNestedStrings(v),
      ]),
    );
  }
  return input;
}
